import { Link } from 'react-router-dom';
import { BookOpen } from 'lucide-react';

export default function Landing() {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-slate-200 z-50">
        <div className="max-w-7xl mx-auto px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <BookOpen className="w-7 h-7 text-library-800" strokeWidth={2.25} />
            <span className="font-serif text-xl font-bold text-slate-900">readwell</span>
          </Link>
          <div className="flex items-center gap-8">
            <a href="#mission" className="text-sm font-medium text-slate-600 hover:text-library-700 transition-colors hidden md:block">Our Mission</a>
            <a href="#why" className="text-sm font-medium text-slate-600 hover:text-library-700 transition-colors hidden md:block">Why Readwell</a>
            <a href="#get-readwell" className="text-sm font-medium text-slate-600 hover:text-library-700 transition-colors hidden md:block">Get Readwell</a>
            <Link to="/compliance" className="text-sm font-medium text-slate-600 hover:text-library-700 transition-colors hidden md:block">Compliance</Link>
            <Link
              to="/login"
              className="bg-library-700 hover:bg-library-800 text-white px-5 py-2 rounded-lg text-sm font-semibold transition-colors"
            >
              Sign In
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-28 px-8 bg-[radial-gradient(ellipse_at_center,_#306b54_0%,_#1e293b_75%)] text-white">
        <div className="relative max-w-4xl mx-auto text-center">
          <div className="inline-block bg-amber-500/20 border border-amber-500/30 text-amber-200 text-xs font-semibold px-4 py-1.5 rounded-full mb-6 tracking-wide">
            Great Books, for Every Student, in Every Format
          </div>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-black mb-6 leading-tight">
            The Power of Classic Literature.<br/>
            <span className="text-library-300">Accessible However Your Students Learn Best.</span>
          </h1>
          <p className="text-lg md:text-xl text-slate-200 max-w-3xl mx-auto mb-10 leading-relaxed">
            For centuries, the written word has been our most powerful teaching tool. Readwell unlocks this power for every student, giving teachers the simplicity of sharing a PDF in all the formats today's students need to thrive. Read it, hear it, learn from it.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to="/district-signup" className="bg-white text-library-800 hover:bg-library-50 px-8 py-3.5 rounded-xl text-base font-semibold transition-all hover:shadow-lg hover:shadow-white/20 hover:-translate-y-0.5">
              Get Started →
            </Link>
            <a href="#mission" className="bg-white/10 hover:bg-white/20 text-white px-8 py-3.5 rounded-xl text-base font-semibold border border-white/20 transition-all hover:-translate-y-0.5">
              Learn More
            </a>
          </div>
        </div>
      </section>

      {/* Quote Banner */}
      <section className="pb-16 px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="bg-amber-50 rounded-3xl px-10 py-6 shadow-xl border border-amber-100 text-center -mt-12 relative z-10">
            <blockquote className="font-serif italic text-xl text-slate-900 leading-relaxed whitespace-nowrap overflow-hidden">
              "A classic is a book that has never finished saying what it has to say."
            </blockquote>
            <p className="mt-2 text-sm text-slate-600 font-medium">— Italo Calvino</p>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-12 px-8 bg-white border-t border-b border-slate-200">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 text-center">
          <div>
            <div className="font-serif text-4xl font-black text-slate-900">67%</div>
            <div className="text-sm text-slate-600 mt-1 font-medium">Of Common Core classics covered</div>
          </div>
          <div>
            <div className="font-serif text-4xl font-black text-library-700">70K+</div>
            <div className="text-sm text-slate-600 mt-1 font-medium">Books in the Readwell catalogue</div>
          </div>
          <div>
            <div className="font-serif text-4xl font-black text-slate-900">25%</div>
            <div className="text-sm text-slate-600 mt-1 font-medium">Of students prefer audio/bimodal</div>
          </div>
          <div>
            <div className="font-serif text-4xl font-black text-library-700">Zero</div>
            <div className="text-sm text-slate-600 mt-1 font-medium">Student data collected — ever</div>
          </div>
        </div>
      </section>

      {/* Mission Section — Three Stakeholders */}
      <section id="mission" className="py-20 px-8 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-xs font-bold uppercase tracking-wider text-library-700 mb-3">Our Mission</div>
          <h2 className="font-serif text-3xl md:text-4xl font-black text-slate-900 mb-4 max-w-4xl leading-tight">
            Our mission is to turn every student into a lifelong reader.
          </h2>
          <p className="text-lg text-slate-600 max-w-3xl mb-12 leading-relaxed">
            Readwell has three stakeholders — Students, Teachers, and Schools. Everything we build serves all three.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white border border-slate-200 rounded-2xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-library-100 rounded-xl flex items-center justify-center text-2xl mb-5">📖</div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Compelling for Students</h3>
              <p className="text-sm text-slate-600 leading-relaxed">Students read more when they can read on their terms. Readwell gives every student text, audio, and bimodal access to 70,000+ classic titles — no prescription, no gatekeeping. Every word highlighted as it's spoken. Students pick their format and start reading.</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center text-2xl mb-5">🎧</div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Easy for Teachers</h3>
              <p className="text-sm text-slate-600 leading-relaxed">Using Readwell is as easy as sharing a link — no new platform to learn, no training, no fuss. Teachers can also create and manage reading assignments directly in the platform. Students get accessible texts; teachers get the workflow they already know.</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-8 hover:shadow-lg transition-shadow">
              <img src="/accessibility-icon.png" alt="" aria-hidden="true" className="w-12 h-12 mb-5" />
              <h3 className="text-lg font-bold text-slate-900 mb-2">Essential for Schools</h3>
              <p className="text-sm text-slate-600 leading-relaxed">WCAG 2.1 AA on every text. VPAT ready for your procurement team. One district license. Zero student data. One Title II checkbox, done.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Readwell — Two Gaps, Snowballing */}
      <section id="why" className="py-20 px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-xs font-bold uppercase tracking-wider text-library-700 mb-3">Why Readwell</div>
          <h2 className="font-serif text-3xl md:text-4xl font-black text-slate-900 mb-4">Two Gaps, Compounding.</h2>
          <p className="text-lg text-slate-600 max-w-3xl mb-12 leading-relaxed">
            Supplemental reading is where teachers work the hardest to reach struggling students — hand-picked texts meant to spark excitement and curiosity. When those texts aren't accessible, the students who need them most are the first ones lost. This is where lifelong readers are lost. Readwell exists to change that.
          </p>

          {/* Two gaps */}
          <div className="grid md:grid-cols-2 gap-6 mb-10">
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8">
              <div className="text-xs font-bold uppercase tracking-wider text-amber-700 mb-2">The Compliance Gap</div>
              <h3 className="font-serif text-xl font-bold text-slate-900 mb-3">Supplemental Readings Are Invisible to Most Compliance Programs</h3>
              <p className="text-sm text-slate-600 leading-relaxed mb-3">
                Districts have invested heavily in making public websites and LMS platforms accessible. But the scanned PDFs, handouts, and web excerpts teachers share outside the core curriculum? Largely untouched.
              </p>
              <p className="text-sm text-slate-600 leading-relaxed">
                Under the DOJ's updated ADA Title II rules, these materials are in scope. They're also where most actual reading instruction happens — so they're where an accessibility gap does the most damage.
              </p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8">
              <div className="text-xs font-bold uppercase tracking-wider text-amber-700 mb-2">The Literacy Gap</div>
              <h3 className="font-serif text-xl font-bold text-slate-900 mb-3">The Students Who Need That Reading Most Can't Access It</h3>
              <p className="text-sm text-slate-600 leading-relaxed mb-3">
                Students with dyslexia and other reading disorders are over-represented at the bottom of every reading benchmark. Most are undiagnosed. All of them need more reading, in the right format — not less.
              </p>
              <p className="text-sm text-slate-600 leading-relaxed">
                When supplemental readings aren't accessible, the students who most need to re-engage with books are the ones pushed out of them. The gap compounds into a snowball.
              </p>
            </div>
          </div>

          {/* Research stats block */}
          <div className="bg-slate-800 rounded-3xl p-8 md:p-12 text-white mb-10">
            <h3 className="font-serif text-2xl md:text-3xl font-black mb-2">The Numbers Behind the Snowball</h3>
            <p className="text-sm text-slate-400 mb-8 max-w-2xl">
              What the research actually shows about the overlap between struggling readers and reading disorders.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div>
                <div className="font-serif text-4xl font-black text-library-300 mb-2">40%</div>
                <div className="text-xs text-slate-300 leading-relaxed">Of U.S. 4th graders scored "Below Basic" on the 2024 NAEP reading assessment — the highest share since 2002<sup className="text-library-300">1</sup></div>
              </div>
              <div>
                <div className="font-serif text-4xl font-black text-library-300 mb-2">70–80%</div>
                <div className="text-xs text-slate-300 leading-relaxed">Of poor readers have weaknesses in phonological processing — the profile of a reading disability<sup className="text-library-300">2</sup></div>
              </div>
              <div>
                <div className="font-serif text-4xl font-black text-library-300 mb-2">74%</div>
                <div className="text-xs text-slate-300 leading-relaxed">Of children with reading problems in 1st grade remain poor readers in 9th grade without explicit intervention<sup className="text-library-300">3</sup></div>
              </div>
            </div>
            <div className="border-t border-white/10 pt-6 grid md:grid-cols-2 gap-6">
              <div>
                <div className="font-serif text-lg font-bold mb-2 text-amber-500">The "Wait to Fail" Model</div>
                <p className="text-sm text-slate-300 leading-relaxed">
                  Most schools wait until a student is significantly behind — usually 3rd grade — before testing for disabilities. By then, students have spent years "faking it" through context clues. Undiagnosed struggling readers are the canaries in the coal mine.
                </p>
              </div>
              <div>
                <div className="font-serif text-lg font-bold mb-2 text-amber-500">The 3rd-Grade Wall</div>
                <p className="text-sm text-slate-300 leading-relaxed">
                  Through 3rd grade, kids are "learning to read." In 4th grade, they switch to "reading to learn." Students who can't yet decode don't just fall behind in reading — they lose access to science, history, and math at the same time.
                </p>
              </div>
            </div>
            <div className="border-t border-white/10 pt-6 mt-6 text-xs text-slate-500 leading-relaxed space-y-1">
              <p><sup className="text-library-300">1</sup> 2024 NAEP Reading Assessment, Grade 4. National Center for Education Statistics. <a href="https://www.nationsreportcard.gov/reports/reading/2024/g4_8/" className="underline hover:text-slate-400" target="_blank" rel="noopener noreferrer">nationsreportcard.gov</a></p>
              <p><sup className="text-library-300">2</sup> Moats, L. & Tolman, C., LETRS research synthesis; corroborated across multiple reading-disability reviews. See <a href="https://www.readingrockets.org/topics/struggling-readers/articles/types-reading-disability" className="underline hover:text-slate-400" target="_blank" rel="noopener noreferrer">Reading Rockets: Types of Reading Disability</a>.</p>
              <p><sup className="text-library-300">3</sup> Shaywitz, S.E. et al. Connecticut Longitudinal Study. Persistence of dyslexia at adolescence. <a href="https://pubmed.ncbi.nlm.nih.gov/10585988/" className="underline hover:text-slate-400" target="_blank" rel="noopener noreferrer">PubMed 10585988</a>.</p>
            </div>
          </div>

          {/* How to Break the Snowball — 3 cards kept */}
          <div className="mb-10">
            <h3 className="font-serif text-2xl md:text-3xl font-black text-slate-900 mb-4">How to Break the Snowball</h3>
            <p className="text-lg text-slate-600 max-w-3xl mb-8 leading-relaxed">
              The research is clear on this too. Two things reliably pull struggling readers back: a <strong>sense of choice</strong> over what and how they read, and <strong>formats that unlock comprehension</strong> even before decoding is automatic. Audio does the second; bimodal does both.
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8">
                <div className="w-12 h-12 bg-library-100 rounded-xl flex items-center justify-center text-2xl mb-5">📚</div>
                <h4 className="text-lg font-bold text-slate-900 mb-2">Choice Rebuilds Engagement</h4>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Struggling readers, particularly those with reading disorders, come alive when you give them real choices over how they read. Letting students choose their format — text, audio, or both — is often the first step toward building a love of books.
                </p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center text-2xl mb-5">🎧</div>
                <h4 className="text-lg font-bold text-slate-900 mb-2">Audio Builds Language</h4>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Listening to fluent narration builds two things at once: phonemic awareness and vocabulary in context. For students still developing decoding skills, audio isn't accommodation — it's instruction.
                </p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8 ring-2 ring-library-500/30">
                <div className="w-12 h-12 bg-library-500 rounded-xl flex items-center justify-center text-2xl mb-5">✨</div>
                <h4 className="text-lg font-bold text-slate-900 mb-2">Bimodal Unlocks Fluency</h4>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Bimodal is the bridge to reading fluency. It's as easy as audio, while teaching scanning, pacing, and word association. Most importantly, it provides two modes through which students can decode and deepen comprehension.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How to Get Readwell in Your School */}
      <section id="get-readwell" className="py-20 px-8 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-xs font-bold uppercase tracking-wider text-library-700 mb-3">Get Readwell</div>
          <h2 className="font-serif text-3xl md:text-4xl font-black text-slate-900 mb-4">How to Get Readwell in Your School</h2>
          <div className="bg-slate-800 rounded-3xl p-8 md:p-12 text-white">
            <div className="max-w-3xl">
              <p className="text-slate-300 leading-relaxed mb-4">Per-student platforms create a perverse incentive: they make accessible formats available only to students with a medical prescription, because every seat costs money. That means 75% of students who could benefit from audio or bimodal reading simply don't get it.</p>
              <p className="text-slate-300 leading-relaxed mb-8">Readwell flips that model. One flat fee covers your entire district, so you can give every student access to the formats that work for them. More students reading more books means more lifelong readers — that's the goal.</p>
            </div>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white/10 border border-white/10 rounded-xl p-6">
                <div className="font-serif text-xl font-black text-library-300 mb-2">1. License</div>
                <p className="text-sm text-slate-300 leading-relaxed">One district license, $1/student for grades 4–12. K-3 free. Net-90 from go-live — nothing due before the deadline.</p>
              </div>
              <div className="bg-white/10 border border-white/10 rounded-xl p-6">
                <div className="font-serif text-xl font-black text-library-300 mb-2">2. Deploy</div>
                <p className="text-sm text-slate-300 leading-relaxed">30-minute setup call. Browser-only — no integration, no IT burden. Your teachers can start assigning literature immediately.</p>
              </div>
              <div className="bg-white/10 border border-white/10 rounded-xl p-6">
                <div className="font-serif text-xl font-black text-library-300 mb-2">3. Read</div>
                <p className="text-sm text-slate-300 leading-relaxed">Teachers share a link. Students enter a three-word access code. They pick their format — text, audio, or bimodal — and start reading. No logins, no PII.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-8 bg-[radial-gradient(ellipse_at_center,_#306b54_0%,_#1e293b_75%)] text-white text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-serif text-3xl md:text-4xl font-black mb-4">Let the Teachers Teach.<br/>Let the Classics Speak.</h2>
          <p className="text-lg text-slate-200 mb-8 leading-relaxed">We do the accessibility work and hand you the docs. Your teachers teach. Your students read. See how it works for your district — no student data required, no obligation.</p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to="/district-signup" className="bg-white text-library-800 hover:bg-library-50 px-8 py-3.5 rounded-xl text-base font-semibold transition-all hover:shadow-lg hover:shadow-white/20 hover:-translate-y-0.5">
              Get Started →
            </Link>
            <Link to="/login" className="bg-white/10 hover:bg-white/20 text-white px-8 py-3.5 rounded-xl text-base font-semibold border border-white/20 transition-all hover:-translate-y-0.5">
              Sign In
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-8 border-t border-slate-200 text-center">
        <p className="text-sm text-slate-600">
          © 2026 Readwell · <a href="https://www.readwell.academy/" className="text-library-700 hover:underline">readwell.academy</a> · Classic Literature, Accessible to Every Student
        </p>
      </footer>
    </div>
  );
}
